import { useTranslation } from 'react-i18next';
import i18n from 'src/i18n';
import { useEffect, useMemo, useState } from 'react';
import { Box, Button, Card, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { Link as RouterLink } from 'react-router-dom';
import HttpServices from 'src/services/httpService';
import { userModuleURL } from 'src/services/urlService';
import { CommonHelper } from 'src/utils/commonHelper';

const firstParam = (params, keys) => {
  for (const key of keys) {
    const value = params.get(key);
    if (value) return value;
  }
  return '';
};

export default function WhatsAppRedirect() {
  useTranslation();
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('Completing WhatsApp connection...');
  const [registrationPIN, setRegistrationPIN] = useState('');
  const [pendingAccountID, setPendingAccountID] = useState(null);

  const payload = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      code: firstParam(params, ['code']),
      waba_id: firstParam(params, ['waba_id', 'whatsapp_business_account_id', 'business_account_id']),
      phone_number_id: firstParam(params, ['phone_number_id', 'phone_id']),
      connection_type: firstParam(params, ['connection_type']) || 'waba',
    };
  }, []);

  useEffect(() => {
    if (!CommonHelper.HasProfessionalPlan()) {
      setStatus('error');
      setMessage(i18n.t('plan_access.whatsapp_upgrade'));
      return;
    }
    if (!payload.code && !payload.waba_id) {
      setStatus('error');
      setMessage('Meta did not return a WhatsApp account id. Please open the WhatsApp card and try again.');
      return;
    }

    HttpServices.Post({
      url: userModuleURL.whatsappConnect,
      body: payload,
      successCallback: (data) => {
        if (data?.registration_required) {
          setPendingAccountID(data.id);
          setStatus('ready');
          setMessage('Enter the six-digit two-step verification PIN for this number.');
          return;
        }
        setStatus('success');
        setMessage('WhatsApp account connected successfully.');
      },
      failureCallback: (errorMessage) => {
        setStatus('error');
        setMessage(errorMessage || 'WhatsApp connection failed.');
      },
    });
  }, [payload]);

  const completeConnection = () => {
    if (!CommonHelper.HasProfessionalPlan()) {
      setStatus('error');
      setMessage(i18n.t('plan_access.whatsapp_upgrade'));
      return;
    }
    if (registrationPIN.length !== 6 || status === 'loading' || !pendingAccountID) return;
    setStatus('loading');
    setMessage('Completing WhatsApp connection...');
    HttpServices.Post({
      url: userModuleURL.whatsappRegister(pendingAccountID),
      body: { pin: registrationPIN },
      successCallback: () => {
        setRegistrationPIN('');
        setStatus('success');
        setMessage('WhatsApp account connected successfully.');
      },
      failureCallback: (errorMessage) => {
        setStatus('ready');
        setMessage(errorMessage || 'WhatsApp connection failed.');
      },
    });
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', bgcolor: '#f8fafc', p: 2 }}>
      <Card variant="outlined" sx={{ width: '100%', maxWidth: 460, p: 3, borderRadius: 1 }}>
        <Stack spacing={2.5} alignItems="center" textAlign="center">
          {status === 'loading' ? <CircularProgress /> : <WhatsAppIcon color={status === 'error' ? 'error' : 'success'} sx={{ fontSize: 44 }} />}
          <Typography variant="h5" fontWeight={800}>
            WhatsApp Integration
          </Typography>
          <Typography color="text.secondary">{message}</Typography>
          {status === 'ready' && (
            <TextField
              fullWidth
              label="Two-step verification PIN"
              type="password"
              value={registrationPIN}
              onChange={(event) => setRegistrationPIN(event.target.value.replace(/\D/g, '').slice(0, 6))}
              inputProps={{ inputMode: 'numeric', maxLength: 6 }}
            />
          )}
          {status === 'ready' && <Button variant="contained" disabled={registrationPIN.length !== 6} onClick={completeConnection}>Connect</Button>}
          <Button component={RouterLink} to="/integrations" variant="contained">
            Back to integrations
          </Button>
        </Stack>
      </Card>
    </Box>
  );
}
